import gql from 'graphql-tag';
import React, {useMemo, useState} from 'react';

import {pluralize} from 'BenchlingUtil';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import SearchablePlateProcedureParameterDropdown from 'samples/plate-maps/display-layer-controls/SearchablePlateProcedureParameterDropdown';
import {
  FillPlatePlannedValuesQuery,
  FillPlatePlannedValuesQueryVariables,
} from 'samples/plate-maps/fill-plate-modal/__generated/FillPlatePlannedValuesQuery';
import {
  FILL_PLATE_LIST_ITEM_BIOENTITY_FRAGMENT,
  PlannedValueItemNode,
  validateFillPlateListItem,
} from 'samples/plate-maps/fill-plate-modal/FillPlateFromModalUtils';
import {ProcedureTestOrderConfig} from 'samples/plate-maps/integrations/procedures/PlateMapTestOrderUtils';
import {PlateMaterialInput} from 'samples/plate-maps/PlateMapConstants';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {SelectableItemsTable, SelectItemsFromListView} from 'worklists/SelectItemsFromListView';

export const FILL_PLATE_PLANNED_VALUES_QUERY = gql`
  query FillPlatePlannedValuesQuery($bioentityIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    bioentities(ids: $bioentityIds) {
      ... on RegistryItemInterface {
        ...FillPlateListItemBioentityFragment
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${FILL_PLATE_LIST_ITEM_BIOENTITY_FRAGMENT}
`;

interface SelectItemsFromPlannedValueModalResult {
  materialInputId: string;
  selectedItems: Array<ExcludeInaccessible<PlannedValueItemNode>>;
}

export interface MaterialInputWithPlannedValueIds {
  materialInput: PlateMaterialInput;
  plannedValueIds: Array<string>;
}

export default function FillPlateFromPlannedMaterialInputModal({
  onRequestClose,
  onRequestSubmit,
  destinationPlateId,
  materialInputsWithPlannedValues,
  procedureTestOrderConfig,
  plateMapType,
}: {
  onRequestClose: () => void;
  onRequestSubmit: (result: SelectItemsFromPlannedValueModalResult) => void;
  destinationPlateId: string | null;
  materialInputsWithPlannedValues: Array<MaterialInputWithPlannedValueIds>;
  procedureTestOrderConfig: ProcedureTestOrderConfig | null;
  plateMapType: PlateMapType;
}): JSX.Element {
  const [selectedMaterialInputId, setSelectedMaterialInputId] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Array<PlannedValueItemNode>>([]);
  const matInputsWithPlannedValuesById = useMemo(
    () => Object.fromEntries(materialInputsWithPlannedValues.map((m) => [m.materialInput.versionId, m])),
    [materialInputsWithPlannedValues]
  );

  const searchInput = (
    <SearchablePlateProcedureParameterDropdown<PlateMaterialInput>
      parameters={materialInputsWithPlannedValues.map((materialInput) => materialInput.materialInput)}
      closeOnInsideClick={true}
      onSelect={(selectedValues) => {
        setSelectedItems([]);
        // The searchable select dropdown only supports an array of selected values, but we only allow one
        // selection here. So, use only the last (most recently selected) value here.
        setSelectedMaterialInputId(selectedValues.slice(-1)[0] ?? null);
      }}
      selectedValues={selectedMaterialInputId == null ? [] : [selectedMaterialInputId]}
      disabled={false}
      getOptionData={(materialInput) => {
        const plannedValueCount =
          matInputsWithPlannedValuesById[materialInput.versionId].plannedValueIds.length;
        return {
          name: materialInput.name,
          value: materialInput.versionId,
          metadata: `${plannedValueCount} ${pluralize(plannedValueCount, 'entity')}`,
        };
      }}
      noneSelectedMessage="Select material input"
    />
  );

  const buttons = [
    {text: 'Cancel', cancel: true},
    {
      text: `Add ${selectedItems.length} ${pluralize(selectedItems.length, 'item')}`,
      style: ButtonStyle.SECONDARY,
      submit: true,
      disabled: selectedItems.length === 0,
      dataTestAttrs: {component: 'AddFromWorklistModal', key: ModalButtonKey.NEXT},
    },
  ];

  return (
    <Modal
      title="Add from planned material inputs"
      onRequestClose={onRequestClose}
      buttons={buttons}
      onRequestSubmit={() => {
        onRequestSubmit({
          materialInputId: selectedMaterialInputId!,
          // Inaccessible items cannot be selected
          selectedItems: selectedItems as Array<ExcludeInaccessible<PlannedValueItemNode>>,
        });
      }}
      dataTestAttrs={{component: 'SelectItemsFromWorklistModal', element: 'root'}}
      expandHeight={true}
    >
      <SelectItemsFromListView<FillPlatePlannedValuesQuery, string, FillPlatePlannedValuesQueryVariables>
        listSummary={selectedMaterialInputId}
        query={FILL_PLATE_PLANNED_VALUES_QUERY}
        queryVariablesFromListSummary={(selectedMaterialInputId: string) => ({
          bioentityIds: matInputsWithPlannedValuesById[selectedMaterialInputId].plannedValueIds,
        })}
        listSearchInput={searchInput}
      >
        {(data): JSX.Element => (
          <SelectableItemsTable<PlannedValueItemNode>
            items={data.bioentities}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            validateItem={(listItemNode: PlannedValueItemNode) =>
              validateFillPlateListItem({
                listItemNode,
                destinationPlateId,
                procedureTestOrderConfig,
                plateMapType,
              })
            }
          />
        )}
      </SelectItemsFromListView>
    </Modal>
  );
}
